import { Coins } from "@phosphor-icons/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { actions } from "astro:actions";
import { isString, sumBy } from "lodash-es";
import { use, useMemo } from "react";
import { objectEntries } from "ts-extras";

import { useLocale } from "~/hooks/locale";
import type { Theme } from "~/theme";

import { PreGameIdContext } from "../../PreGameIdContext";
import { type ThemeDetails, ThemeOption } from "./ThemeOption";

const catalog: Record<Theme, ThemeDetails> = {
  knoword: {
    name: "Light",
    price: 0,
  },
  dark: {
    name: "Dark",
    price: 0,
  },
  water: {
    name: "Aqualight",
    description: "Cool aquatic tones",
    price: 1000,
  },
  hearts: {
    name: "Sweetheart",
    description: "Cute pastel pinks",
    price: 1000,
  },
  clouds: {
    name: "Skyward",
    description: "Bright airy blues",
    price: 1000,
  },
  dunes: {
    name: "Ember Glow",
    description: "Warm desert hues",
    price: 1000,
  },
};

export const ThemeStore: React.FC = () => {
  const { t } = useLocale();
  const id = use(PreGameIdContext);

  const { data } = useSuspenseQuery({
    queryKey: ["transactions", id],
    queryFn: actions.me.getTransactions.orThrow,
    staleTime: Infinity,
  });

  const balance = useMemo(
    () => (data ? sumBy(data, (transaction) => transaction.amount) : 0),
    [data],
  );

  return (
    <>
      <div className="space-y-4 @container">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="title title-sm">{t("theme")}</h2>
          {data === null ? (
            <div className="text-sm">{t("themes_logged_out")}</div>
          ) : (
            <div className="flex items-center gap-2">
              {balance.toLocaleString()}
              <Coins className="size-6" />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 @md:grid-cols-3">
          {objectEntries(catalog).map(([key, details]) => (
            <ThemeOption
              key={key}
              themeKey={key}
              details={details}
              balance={balance}
              unlockedThemes={data
                ?.map((transaction) => transaction.memo)
                .filter(isString)}
            />
          ))}
        </div>
      </div>
    </>
  );
};
